import Text from '@cartwhl/ui/base/Text';
import CharacterIcon from '@cartwhl/ui/icons/CharacterIcon';
import HistoryIcon from '@cartwhl/ui/icons/HistoryIcon';
import MoveIcon from '@cartwhl/ui/icons/MoveIcon';
import RedoIcon from '@cartwhl/ui/icons/RedoIcon';
import ResetIcon from '@cartwhl/ui/icons/ResetIcon';
import RotateIcon from '@cartwhl/ui/icons/RotateIcon';
import UndoIcon from '@cartwhl/ui/icons/UndoIcon';

import type { CartographerHistoryItem } from '../../lib/history/cartographerHistory';
import ExcludedBoneIcon from '../../presentations/icons/ExcludedBoneIcon';
import MappingIcon from '../../presentations/icons/MappingIcon';
import PropertiesIcon from '../../presentations/icons/PropertiesIcon';
import SolverIcon from '../../presentations/icons/SolverIcon';

interface CartographerHistoryPanelProps {
  historyItems: CartographerHistoryItem[];
  historyIndex: number;
  onSelectHistory: (index: number) => void;
  onUndo: () => void;
  onRedo: () => void;
  undoDisabled?: boolean;
  redoDisabled?: boolean;
}

function getHistoryItemIcon(item: CartographerHistoryItem) {
  const label = item.label.toLowerCase();

  if (label.includes('reset')) {
    return <ResetIcon className="h-4 w-4" />;
  }

  switch (item.kind) {
    case 'baseline':
      if (label.includes('loaded character')) {
        return <CharacterIcon className="h-4 w-4" />;
      }
      return <HistoryIcon className="h-4 w-4" />;
    case 'mapping':
      if (label.includes('clear mapping')) {
        return <MappingIcon className="h-4 w-4" dotVisible={false} />;
      }
      if (label.includes('solver')) {
        return <SolverIcon className="h-4 w-4" />;
      }
      if (label.includes('bone exclusion') || label.includes('excluded bone')) {
        return <ExcludedBoneIcon className="h-4 w-4" />;
      }
      return <MappingIcon className="h-4 w-4" />;
    case 'pose':
      if (label.includes('translation') || label.includes('translate')) {
        return <MoveIcon className="h-4 w-4" />;
      }
      if (label.includes('rotation') || label.includes('rotate')) {
        return <RotateIcon className="h-4 w-4" />;
      }
      return <HistoryIcon className="h-4 w-4" />;
    case 'ik':
      if (label.includes('translation') || label.includes('translate')) {
        return <MoveIcon className="h-4 w-4" />;
      }
      return <RotateIcon className="h-4 w-4" />;
    case 'bone-properties':
      return <PropertiesIcon className="h-4 w-4" />;
    default:
      return <HistoryIcon className="h-4 w-4" />;
  }
}

function getHistoryKindLabel(kind: CartographerHistoryItem['kind']) {
  switch (kind) {
    case 'baseline':
      return 'Baseline';
    case 'mapping':
      return 'Config edit';
    case 'pose':
      return 'Pose edit';
    case 'bone-properties':
      return 'Property edit';
    case 'ik':
      return 'IK edit';
    default:
      return 'History edit';
  }
}

export default function CartographerHistoryPanel({
  historyItems,
  historyIndex,
  onSelectHistory,
  onUndo,
  onRedo,
  undoDisabled = false,
  redoDisabled = false,
}: CartographerHistoryPanelProps) {
  const reversedItems = historyItems
    .map((item, index) => ({ item, index }))
    .reverse();

  return (
    <div className="dark:bg-gray-90/20 flex h-full min-h-0 flex-col rounded-xl bg-white/20 p-1.5">
      <div className="translucent-white-scrollbar min-h-0 flex-1 overflow-y-auto pr-0.5">
        {historyItems.length === 0 ? (
          <div className="flex h-full items-center justify-center p-3">
            <Text variant="studio/body/sm" className="text-secondary text-center">
              No history snapshots yet.
            </Text>
          </div>
        ) : (
          <div className="flex flex-col gap-0.5">
            {reversedItems.map(({ item, index }) => {
              const isActive = index === historyIndex;

              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onSelectHistory(index)}
                  className={`text-primary w-full rounded-lg px-2 py-1 text-left transition-colors ${
                    isActive
                      ? 'bg-primary shadow-sm'
                      : 'hover:bg-white/50 dark:hover:bg-gray-90/50'
                  }`}
                  aria-pressed={isActive}
                  aria-label={`${getHistoryKindLabel(item.kind)}: ${item.label}${item.saved ? ', saved' : ''}`}
                >
                  <div className="flex min-w-0 items-center gap-1.5">
                    <span
                      className="flex h-5 w-5 shrink-0 items-center justify-center text-primary"
                      title={getHistoryKindLabel(item.kind)}
                    >
                      {getHistoryItemIcon(item)}
                    </span>
                    <Text
                      variant="studio/body/medium/sm"
                      className="min-w-0 flex-1 truncate"
                    >
                      {item.label}
                    </Text>
                    {item.saved && (
                      <span className="bg-blue-50/12 shrink-0 rounded-md px-1 py-0.5">
                        <Text
                          variant="studio/body/medium/xs"
                          className="text-blue-60"
                        >
                          Saved
                        </Text>
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="border-primary mt-0.5 flex shrink-0 items-center gap-1 border-t pt-0.5">
        <button
          type="button"
          onClick={onUndo}
          disabled={undoDisabled}
          className={`focus-visible inline-flex h-6 flex-1 items-center justify-center gap-1 rounded-md border px-2 transition-colors ${
            undoDisabled
              ? 'border-primary bg-secondary text-tertiary cursor-not-allowed opacity-70'
              : 'border-primary bg-primary text-primary hover:bg-secondary'
          }`}
          aria-label="Undo"
        >
          <UndoIcon className="h-3 w-3" />
          <Text as="span" variant="studio/body/medium/xs">
            Undo
          </Text>
        </button>
        <button
          type="button"
          onClick={onRedo}
          disabled={redoDisabled}
          className={`focus-visible inline-flex h-6 flex-1 items-center justify-center gap-1 rounded-md border px-2 transition-colors ${
            redoDisabled
              ? 'border-primary bg-secondary text-tertiary cursor-not-allowed opacity-70'
              : 'border-primary bg-primary text-primary hover:bg-secondary'
          }`}
          aria-label="Redo"
        >
          <RedoIcon className="h-3 w-3" />
          <Text as="span" variant="studio/body/medium/xs">
            Redo
          </Text>
        </button>
      </div>
    </div>
  );
}
